/**
 * Guía de Kubo hacia un lugar exacto de la pantalla: espera a que aparezca
 * (el módulo o la pestaña pueden estar cargando), lo centra en la vista, lo
 * resalta con un anillo y le pone encima una etiqueta "Completa aquí" que
 * sigue al elemento mientras se desplaza la página. Si es un campo de texto,
 * deja el cursor en él. Todo se limpia solo al interactuar o a los 9 s.
 */
export interface KuboTarget {
  /** Uno o varios selectores (separados por coma), en orden de preferencia. */
  selector: string;
  /** Texto de la etiqueta sobre el elemento. */
  label?: string;
  /** Elegir el primero cuyo campo esté vacío (p. ej. razón social o teléfono). */
  firstEmpty?: boolean;
  /** Si el destino aún no existe (p. ej. está dentro de un modal cerrado), se
   *  señala esto otro (el botón que lo abre). */
  fallback?: KuboTarget;
  /** Al tocar lo señalado, se continúa guiando hacia aquí (p. ej. «Editar» → campo). */
  next?: KuboTarget;
}

/** Capas de modal: si el destino está dentro de una, la etiqueta va encima. */
const MODAL_LAYERS = '[aria-modal="true"], .modal-overlay, .settings-modal-overlay, .dialog-overlay';

const WAIT_MS = 8000;
const SHOW_MS = 9000;

let current: { el: HTMLElement; pin: HTMLElement; cleanup: () => void } | null = null;

function visible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
}

function fieldOf(el: HTMLElement): HTMLInputElement | HTMLTextAreaElement | null {
  if (el.matches('input, textarea')) return el as HTMLInputElement;
  return el.querySelector('input:not([type=file]):not([type=hidden]), textarea');
}

function find(target: KuboTarget): HTMLElement | null {
  const groups = target.selector.split(',').map(sel => sel.trim()).filter(Boolean);
  if (target.firstEmpty) {
    const all = groups.flatMap(sel => Array.from(document.querySelectorAll<HTMLElement>(sel))).filter(visible);
    return all.find(el => !String(fieldOf(el)?.value ?? '').trim()) ?? all[0] ?? null;
  }
  // En orden de preferencia: el primer selector que tenga algo visible.
  for (const sel of groups) {
    const el = Array.from(document.querySelectorAll<HTMLElement>(sel)).find(visible);
    if (el) return el;
  }
  return null;
}

export function clearKuboGuide(): void {
  current?.cleanup();
  current = null;
}

/** Devuelve una promesa que se resuelve con true si encontró y señaló el elemento. */
export function guideKuboTo(target: KuboTarget): Promise<boolean> {
  clearKuboGuide();
  return new Promise(resolve => {
    const started = Date.now();
    const attempt = () => {
      const main = find(target);
      const fallback = !main && target.fallback ? find(target.fallback) : null;
      const el = main ?? fallback;
      if (!el) {
        if (Date.now() - started < WAIT_MS) { setTimeout(attempt, 150); return; }
        resolve(false);
        return;
      }
      spotlight(el, main ? target : target.fallback!);
      resolve(true);
    };
    // Un respiro para que el router y la pestaña terminen de cambiar.
    setTimeout(attempt, 250);
  });
}

function spotlight(el: HTMLElement, target: KuboTarget): void {
  const label = target.label || 'Completa aquí';
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('kubo-spotlight');
  const field = fieldOf(el);
  if (field && el.matches('input, textarea, .form-group, .form-group *')) setTimeout(() => field.focus({ preventScroll: true }), 450);

  const pin = document.createElement('div');
  pin.className = el.closest(MODAL_LAYERS) ? 'kubo-pin in-modal' : 'kubo-pin';
  pin.setAttribute('role', 'status');
  pin.innerHTML = '<span class="kubo-pin-dot" aria-hidden="true"></span><span></span>';
  (pin.lastElementChild as HTMLElement).textContent = label;
  document.body.appendChild(pin);

  let frame = 0;
  const place = () => {
    frame = 0;
    const r = el.getBoundingClientRect();
    const offscreen = r.bottom < 0 || r.top > window.innerHeight;
    pin.classList.toggle('is-hidden', offscreen);
    const below = r.top < 64;
    pin.classList.toggle('is-below', below);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - pin.offsetWidth - 8);
    pin.style.left = `${left}px`;
    pin.style.top = below ? `${r.bottom + 10}px` : `${r.top - pin.offsetHeight - 10}px`;
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(place); };
  place();
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  // Se va sola al empezar a completar (o al tocar el elemento) o a los 9 s.
  const done = () => clearKuboGuide();
  // Al tocarlo, si hay un paso siguiente (p. ej. el campo dentro del modal que
  // abre «Editar»), Kubo sigue guiando hasta allí.
  const clicked = () => { clearKuboGuide(); if (target.next) setTimeout(() => void guideKuboTo(target.next!), 200); };
  el.addEventListener('input', done, { once: true });
  el.addEventListener('change', done, { once: true });
  el.addEventListener('click', clicked, { once: true });
  const timer = setTimeout(done, SHOW_MS);

  current = {
    el,
    pin,
    cleanup: () => {
      clearTimeout(timer);
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', schedule);
      el.removeEventListener('input', done);
      el.removeEventListener('change', done);
      el.removeEventListener('click', clicked);
      el.classList.remove('kubo-spotlight');
      pin.remove();
    },
  };
}
